import { useDesktopNavigation } from "@app/components/navigation/DesktopNavigationContext";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import { BarHeader, cn, Icon, LayoutLeft } from "@dust-tt/sparkle";
import type React from "react";

interface AppLayoutTitleProps {
  children?: React.ReactNode;
  className?: string;
}

export function AppLayoutTitle({ children, className }: AppLayoutTitleProps) {
  const isMobile = useIsMobile();
  const { isNavigationBarOpen, setIsNavigationBarOpen } =
    useDesktopNavigation();
  const showOpenNav = !isMobile && !isNavigationBarOpen;

  return (
    <div
      className={cn(
        "relative z-20 shrink-0",
        !children && !showOpenNav && "md:hidden"
      )}
    >
      {/* Claude-style soft wash: solid near the title, dissolves into the canvas. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[4.75rem] bg-gradient-to-b from-background from-35% via-background/45 to-transparent"
      />
      <div
        className={cn(
          "relative flex h-title w-full items-center",
          showOpenNav ? "gap-1 px-2 md:pr-4" : "px-4 pl-14 md:pl-3",
          className
        )}
      >
        {showOpenNav && (
          <button
            type="button"
            aria-label="Open sidebar"
            title="Open sidebar"
            onClick={() => setIsNavigationBarOpen(true)}
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
              "text-foreground outline-none transition-colors",
              "hover:bg-hover focus-visible:bg-hover focus-visible:ring-2 focus-visible:ring-ring"
            )}
          >
            <Icon visual={LayoutLeft} size="sm" />
          </button>
        )}
        {children ? (
          <div className="h-full min-w-0 flex-1">{children}</div>
        ) : null}
      </div>
    </div>
  );
}

export function AppLayoutSimpleCloseTitle({
  title,
  onClose,
}: {
  title: string;
  onClose: () => void;
}) {
  return (
    <AppLayoutTitle>
      <BarHeader
        title={title}
        rightActions={<BarHeader.ButtonBar variant="close" onClose={onClose} />}
        className="ml-10 lg:ml-0"
      />
    </AppLayoutTitle>
  );
}
